/**
 * Monthly series for the measurable inputs of the three-year model — the Metric Trends tab
 * (quick-260928-lh8 follow-up 2026-10-01; widened to every measurable input by quick-261002-be8; re-based to the
 * calendar YEAR TO DATE by quick-261013-hqb, John 2026-10-13: "extend the metrics to cover ytd, looking at each
 * aggregated quarter" — the section rolls these months up by quarter via shared/plan2027/trend-periods).
 * Generated by ~/funnel-benchmarks/trends-live.js from compact Salesforce pulls in ~/funnel-benchmarks/live/
 * (events with TYPEOF Who, converted inbound leads, lead counts, Product opps, call dispositions, deals since 1 Jan,
 * and conversations by caller role — live/SOQL-ytd.md);
 * refresh after month-end = re-run the six compact SOQL, node trends-live.js, node emit-trends-ts.js <this file>.
 * A month flagged partialMonth ends before its last day; immature = the cohort is under the 60-day maturation window,
 * so its lead→booked can still rise. Deal-economics points are New Business only (the model is new-logo) and carry
 * tiny n — the n label under each month is the honest read.
 */
export interface TrendPoint {
  value: number | null;
  num: number;
  den: number;
  immature?: boolean;
}
export interface TrendMonth {
  month: string;
  partialMonth: boolean;
  /** Inbound: booked / matured non-DQ inbound leads by source month (M1). */
  leadToBooked: TrendPoint;
  inboundDeliveredOfBooked: TrendPoint;
  inboundMtgToOpp: TrendPoint;
  /** Outbound conversations in the month (volume; the model input is per seller — see conversationsPerSeller). */
  conversationsPerMonth: TrendPoint;
  conversationsPerMeeting: TrendPoint;
  outboundDeliveredOfDue: TrendPoint;
  outboundMtgToOpp: TrendPoint;
  newLogoShareOfOpps: TrendPoint;
  /** Non-disqualified inbound leads by Latest_Source_Date__c month (den = 1). */
  inboundLeadsPerMonth: TrendPoint;
  /**
   * The quota sellers' OWN conversations (AE + Sales Manager callers) ÷ quota-carrying sellers (den = sellers).
   * BDR conversations are split out since quick-261029-fkq.
   */
  conversationsPerSeller: TrendPoint;
  /** BDR conversations ÷ BDRs who logged one that month (quick-261029-fkq; den = BDRs). */
  conversationsPerBdr: TrendPoint;
  /** New Business opps created in the month: mean Amount (num = sum, den = count with an Amount). */
  avgCreatedOppValue: TrendPoint;
  /** New Business closed in the month: Closed Won ÷ (Closed Won + Closed Lost) by stage. */
  newLogoWinRate: TrendPoint;
  /** New Business won in the month: mean Amount (num = sum, den = count). */
  avgWonValue: TrendPoint;
  /** New Business won in the month: median created→close days (num = sum of days, den = count). */
  cycleDays: TrendPoint;
}
export interface FunnelTrends {
  pulledOn: string;
  firstMonth: string;
  matureDays: number;
  /** Quota-carrying sellers used for conversationsPerSeller. */
  sellers: number;
  series: TrendMonth[];
}

export const FUNNEL_TRENDS: FunnelTrends = {
  "pulledOn": "2026-10-30",
  "firstMonth": "2026-01",
  "matureDays": 60,
  "sellers": 5,
  "series": [
    {
      "month": "2026-01",
      "partialMonth": false,
      "leadToBooked": {
        "value": 0.08571428571428572,
        "num": 9,
        "den": 105,
        "immature": false
      },
      "inboundDeliveredOfBooked": {
        "value": 1,
        "num": 9,
        "den": 9
      },
      "inboundMtgToOpp": {
        "value": 0.45454545454545453,
        "num": 5,
        "den": 11
      },
      "conversationsPerMonth": {
        "value": 405,
        "num": 405,
        "den": 4886
      },
      "conversationsPerMeeting": {
        "value": 27,
        "num": 405,
        "den": 15
      },
      "outboundDeliveredOfDue": {
        "value": 0.6111111111111112,
        "num": 11,
        "den": 18
      },
      "outboundMtgToOpp": {
        "value": 0.09090909090909091,
        "num": 1,
        "den": 11
      },
      "newLogoShareOfOpps": {
        "value": 0.6666666666666666,
        "num": 10,
        "den": 15
      },
      "inboundLeadsPerMonth": {
        "value": 105,
        "num": 105,
        "den": 1
      },
      "conversationsPerSeller": {
        "value": 43.6,
        "num": 218,
        "den": 5
      },
      "conversationsPerBdr": {
        "value": 93.5,
        "num": 187,
        "den": 2
      },
      "avgCreatedOppValue": {
        "value": 96050,
        "num": 960500,
        "den": 10
      },
      "newLogoWinRate": {
        "value": 0,
        "num": 0,
        "den": 8
      },
      "avgWonValue": {
        "value": null,
        "num": 0,
        "den": 0
      },
      "cycleDays": {
        "value": null,
        "num": 0,
        "den": 0
      }
    },
    {
      "month": "2026-02",
      "partialMonth": false,
      "leadToBooked": {
        "value": 0.05,
        "num": 4,
        "den": 80,
        "immature": false
      },
      "inboundDeliveredOfBooked": {
        "value": 0.75,
        "num": 3,
        "den": 4
      },
      "inboundMtgToOpp": {
        "value": 0.2857142857142857,
        "num": 2,
        "den": 7
      },
      "conversationsPerMonth": {
        "value": 492,
        "num": 492,
        "den": 6018
      },
      "conversationsPerMeeting": {
        "value": 32.8,
        "num": 492,
        "den": 15
      },
      "outboundDeliveredOfDue": {
        "value": 0.4666666666666667,
        "num": 7,
        "den": 15
      },
      "outboundMtgToOpp": {
        "value": 0.42857142857142855,
        "num": 3,
        "den": 7
      },
      "newLogoShareOfOpps": {
        "value": 0.7,
        "num": 7,
        "den": 10
      },
      "inboundLeadsPerMonth": {
        "value": 80,
        "num": 80,
        "den": 1
      },
      "conversationsPerSeller": {
        "value": 56.8,
        "num": 284,
        "den": 5
      },
      "conversationsPerBdr": {
        "value": 104,
        "num": 208,
        "den": 2
      },
      "avgCreatedOppValue": {
        "value": 100000,
        "num": 600000,
        "den": 6
      },
      "newLogoWinRate": {
        "value": 0.25,
        "num": 1,
        "den": 4
      },
      "avgWonValue": {
        "value": 45000,
        "num": 45000,
        "den": 1
      },
      "cycleDays": {
        "value": 23,
        "num": 23,
        "den": 1
      }
    },
    {
      "month": "2026-03",
      "partialMonth": false,
      "leadToBooked": {
        "value": 0.08064516129032258,
        "num": 5,
        "den": 62,
        "immature": false
      },
      "inboundDeliveredOfBooked": {
        "value": 0.6,
        "num": 3,
        "den": 5
      },
      "inboundMtgToOpp": {
        "value": 0.26666666666666666,
        "num": 4,
        "den": 15
      },
      "conversationsPerMonth": {
        "value": 620,
        "num": 620,
        "den": 7326
      },
      "conversationsPerMeeting": {
        "value": 24.8,
        "num": 620,
        "den": 25
      },
      "outboundDeliveredOfDue": {
        "value": 0.5652173913043478,
        "num": 13,
        "den": 23
      },
      "outboundMtgToOpp": {
        "value": 0.15384615384615385,
        "num": 2,
        "den": 13
      },
      "newLogoShareOfOpps": {
        "value": 1,
        "num": 5,
        "den": 5
      },
      "inboundLeadsPerMonth": {
        "value": 62,
        "num": 62,
        "den": 1
      },
      "conversationsPerSeller": {
        "value": 89.2,
        "num": 446,
        "den": 5
      },
      "conversationsPerBdr": {
        "value": 87,
        "num": 174,
        "den": 2
      },
      "avgCreatedOppValue": {
        "value": 62000,
        "num": 310000,
        "den": 5
      },
      "newLogoWinRate": {
        "value": 0.1,
        "num": 1,
        "den": 10
      },
      "avgWonValue": {
        "value": 135000,
        "num": 135000,
        "den": 1
      },
      "cycleDays": {
        "value": 684,
        "num": 684,
        "den": 1
      }
    },
    {
      "month": "2026-04",
      "partialMonth": false,
      "leadToBooked": {
        "value": 0.04716981132075472,
        "num": 5,
        "den": 106,
        "immature": false
      },
      "inboundDeliveredOfBooked": {
        "value": 0.8,
        "num": 4,
        "den": 5
      },
      "inboundMtgToOpp": {
        "value": 0.19047619047619047,
        "num": 4,
        "den": 21
      },
      "conversationsPerMonth": {
        "value": 322,
        "num": 322,
        "den": 7250
      },
      "conversationsPerMeeting": {
        "value": 20.125,
        "num": 322,
        "den": 16
      },
      "outboundDeliveredOfDue": {
        "value": 0.5,
        "num": 9,
        "den": 18
      },
      "outboundMtgToOpp": {
        "value": 0.4444444444444444,
        "num": 4,
        "den": 9
      },
      "newLogoShareOfOpps": {
        "value": 0.6,
        "num": 6,
        "den": 10
      },
      "inboundLeadsPerMonth": {
        "value": 106,
        "num": 106,
        "den": 1
      },
      "conversationsPerSeller": {
        "value": 36.8,
        "num": 184,
        "den": 5
      },
      "conversationsPerBdr": {
        "value": 69,
        "num": 138,
        "den": 2
      },
      "avgCreatedOppValue": {
        "value": 115833.33333333333,
        "num": 695000,
        "den": 6
      },
      "newLogoWinRate": {
        "value": 0.3333333333333333,
        "num": 2,
        "den": 6
      },
      "avgWonValue": {
        "value": 60625,
        "num": 121250,
        "den": 2
      },
      "cycleDays": {
        "value": 124.5,
        "num": 249,
        "den": 2
      }
    },
    {
      "month": "2026-05",
      "partialMonth": false,
      "leadToBooked": {
        "value": 0.09352517985611511,
        "num": 13,
        "den": 139,
        "immature": false
      },
      "inboundDeliveredOfBooked": {
        "value": 0.6153846153846154,
        "num": 8,
        "den": 13
      },
      "inboundMtgToOpp": {
        "value": 0.2857142857142857,
        "num": 4,
        "den": 14
      },
      "conversationsPerMonth": {
        "value": 105,
        "num": 105,
        "den": 3375
      },
      "conversationsPerMeeting": {
        "value": 7.5,
        "num": 105,
        "den": 14
      },
      "outboundDeliveredOfDue": {
        "value": 0.2777777777777778,
        "num": 5,
        "den": 18
      },
      "outboundMtgToOpp": {
        "value": 0.6,
        "num": 3,
        "den": 5
      },
      "newLogoShareOfOpps": {
        "value": 0.8333333333333334,
        "num": 10,
        "den": 12
      },
      "inboundLeadsPerMonth": {
        "value": 139,
        "num": 139,
        "den": 1
      },
      "conversationsPerSeller": {
        "value": 8.8,
        "num": 44,
        "den": 5
      },
      "conversationsPerBdr": {
        "value": 30.5,
        "num": 61,
        "den": 2
      },
      "avgCreatedOppValue": {
        "value": 97542.85714285714,
        "num": 682800,
        "den": 7
      },
      "newLogoWinRate": {
        "value": 0,
        "num": 0,
        "den": 2
      },
      "avgWonValue": {
        "value": null,
        "num": 0,
        "den": 0
      },
      "cycleDays": {
        "value": null,
        "num": 0,
        "den": 0
      }
    },
    {
      "month": "2026-06",
      "partialMonth": false,
      "leadToBooked": {
        "value": 0.07,
        "num": 7,
        "den": 100,
        "immature": false
      },
      "inboundDeliveredOfBooked": {
        "value": 0.42857142857142855,
        "num": 3,
        "den": 7
      },
      "inboundMtgToOpp": {
        "value": 0.625,
        "num": 5,
        "den": 8
      },
      "conversationsPerMonth": {
        "value": 456,
        "num": 456,
        "den": 9765
      },
      "conversationsPerMeeting": {
        "value": 15.2,
        "num": 456,
        "den": 30
      },
      "outboundDeliveredOfDue": {
        "value": 0.64,
        "num": 16,
        "den": 25
      },
      "outboundMtgToOpp": {
        "value": 0.3125,
        "num": 5,
        "den": 16
      },
      "newLogoShareOfOpps": {
        "value": 1,
        "num": 8,
        "den": 8
      },
      "inboundLeadsPerMonth": {
        "value": 100,
        "num": 100,
        "den": 1
      },
      "conversationsPerSeller": {
        "value": 23,
        "num": 115,
        "den": 5
      },
      "conversationsPerBdr": {
        "value": 85.25,
        "num": 341,
        "den": 4
      },
      "avgCreatedOppValue": {
        "value": 130000,
        "num": 1040000,
        "den": 8
      },
      "newLogoWinRate": {
        "value": 0.08333333333333333,
        "num": 1,
        "den": 12
      },
      "avgWonValue": {
        "value": 72800,
        "num": 72800,
        "den": 1
      },
      "cycleDays": {
        "value": 45,
        "num": 45,
        "den": 1
      }
    },
    {
      "month": "2026-07",
      "partialMonth": false,
      "leadToBooked": {
        "value": 0.14084507042253522,
        "num": 10,
        "den": 71,
        "immature": false
      },
      "inboundDeliveredOfBooked": {
        "value": 0.7,
        "num": 7,
        "den": 10
      },
      "inboundMtgToOpp": {
        "value": 0.42857142857142855,
        "num": 3,
        "den": 7
      },
      "conversationsPerMonth": {
        "value": 630,
        "num": 630,
        "den": 14031
      },
      "conversationsPerMeeting": {
        "value": 15.75,
        "num": 630,
        "den": 40
      },
      "outboundDeliveredOfDue": {
        "value": 0.425,
        "num": 17,
        "den": 40
      },
      "outboundMtgToOpp": {
        "value": 0.11764705882352941,
        "num": 2,
        "den": 17
      },
      "newLogoShareOfOpps": {
        "value": 0.7272727272727273,
        "num": 8,
        "den": 11
      },
      "inboundLeadsPerMonth": {
        "value": 71,
        "num": 71,
        "den": 1
      },
      "conversationsPerSeller": {
        "value": 47.8,
        "num": 239,
        "den": 5
      },
      "conversationsPerBdr": {
        "value": 97.75,
        "num": 391,
        "den": 4
      },
      "avgCreatedOppValue": {
        "value": 98750,
        "num": 790000,
        "den": 8
      },
      "newLogoWinRate": {
        "value": 0,
        "num": 0,
        "den": 7
      },
      "avgWonValue": {
        "value": null,
        "num": 0,
        "den": 0
      },
      "cycleDays": {
        "value": null,
        "num": 0,
        "den": 0
      }
    },
    {
      "month": "2026-08",
      "partialMonth": false,
      "leadToBooked": {
        "value": 0.038461538461538464,
        "num": 3,
        "den": 78,
        "immature": false
      },
      "inboundDeliveredOfBooked": {
        "value": 0.3333333333333333,
        "num": 1,
        "den": 3
      },
      "inboundMtgToOpp": {
        "value": 0,
        "num": 0,
        "den": 4
      },
      "conversationsPerMonth": {
        "value": 443,
        "num": 443,
        "den": 10684
      },
      "conversationsPerMeeting": {
        "value": 9.844444444444445,
        "num": 443,
        "den": 45
      },
      "outboundDeliveredOfDue": {
        "value": 0.6190476190476191,
        "num": 26,
        "den": 42
      },
      "outboundMtgToOpp": {
        "value": 0.23076923076923078,
        "num": 6,
        "den": 26
      },
      "newLogoShareOfOpps": {
        "value": 0.8,
        "num": 8,
        "den": 10
      },
      "inboundLeadsPerMonth": {
        "value": 78,
        "num": 78,
        "den": 1
      },
      "conversationsPerSeller": {
        "value": 39.8,
        "num": 199,
        "den": 5
      },
      "conversationsPerBdr": {
        "value": 61,
        "num": 244,
        "den": 4
      },
      "avgCreatedOppValue": {
        "value": 130714.28571428571,
        "num": 915000,
        "den": 7
      },
      "newLogoWinRate": {
        "value": 0,
        "num": 0,
        "den": 1
      },
      "avgWonValue": {
        "value": null,
        "num": 0,
        "den": 0
      },
      "cycleDays": {
        "value": null,
        "num": 0,
        "den": 0
      }
    },
    {
      "month": "2026-09",
      "partialMonth": false,
      "leadToBooked": {
        "value": 0.03508771929824561,
        "num": 2,
        "den": 57,
        "immature": true
      },
      "inboundDeliveredOfBooked": {
        "value": 1,
        "num": 2,
        "den": 2
      },
      "inboundMtgToOpp": {
        "value": 0.6666666666666666,
        "num": 2,
        "den": 3
      },
      "conversationsPerMonth": {
        "value": 932,
        "num": 932,
        "den": 15991
      },
      "conversationsPerMeeting": {
        "value": 19.020408163265305,
        "num": 932,
        "den": 49
      },
      "outboundDeliveredOfDue": {
        "value": 0.43478260869565216,
        "num": 20,
        "den": 46
      },
      "outboundMtgToOpp": {
        "value": 0,
        "num": 0,
        "den": 20
      },
      "newLogoShareOfOpps": {
        "value": 0.75,
        "num": 3,
        "den": 4
      },
      "inboundLeadsPerMonth": {
        "value": 57,
        "num": 57,
        "den": 1
      },
      "conversationsPerSeller": {
        "value": 70.8,
        "num": 354,
        "den": 5
      },
      "conversationsPerBdr": {
        "value": 144.5,
        "num": 578,
        "den": 4
      },
      "avgCreatedOppValue": {
        "value": 93333.33333333333,
        "num": 280000,
        "den": 3
      },
      "newLogoWinRate": {
        "value": 0.16666666666666666,
        "num": 1,
        "den": 6
      },
      "avgWonValue": {
        "value": 100000,
        "num": 100000,
        "den": 1
      },
      "cycleDays": {
        "value": 170,
        "num": 170,
        "den": 1
      }
    },
    {
      "month": "2026-10",
      "partialMonth": false,
      "leadToBooked": {
        "value": 0.0196078431372549,
        "num": 1,
        "den": 51,
        "immature": true
      },
      "inboundDeliveredOfBooked": {
        "value": 0,
        "num": 0,
        "den": 1
      },
      "inboundMtgToOpp": {
        "value": null,
        "num": 0,
        "den": 0
      },
      "conversationsPerMonth": {
        "value": 796,
        "num": 796,
        "den": 13732
      },
      "conversationsPerMeeting": {
        "value": 15.307692307692308,
        "num": 796,
        "den": 52
      },
      "outboundDeliveredOfDue": {
        "value": 0.41509433962264153,
        "num": 22,
        "den": 53
      },
      "outboundMtgToOpp": {
        "value": 0.22727272727272727,
        "num": 5,
        "den": 22
      },
      "newLogoShareOfOpps": {
        "value": 0.875,
        "num": 7,
        "den": 8
      },
      "inboundLeadsPerMonth": {
        "value": 51,
        "num": 51,
        "den": 1
      },
      "conversationsPerSeller": {
        "value": 55.4,
        "num": 277,
        "den": 5
      },
      "conversationsPerBdr": {
        "value": 129.75,
        "num": 519,
        "den": 4
      },
      "avgCreatedOppValue": {
        "value": 169285.7142857143,
        "num": 1185000,
        "den": 7
      },
      "newLogoWinRate": {
        "value": 0,
        "num": 0,
        "den": 1
      },
      "avgWonValue": {
        "value": null,
        "num": 0,
        "den": 0
      },
      "cycleDays": {
        "value": null,
        "num": 0,
        "den": 0
      }
    }
  ]
};
